import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultationModal from '../components/consultation/ConsultationModal';
import {
  ArrowRight,
  Lightbulb,
  Ruler,
  Palette,
  Sofa,
  Sun,
  PenTool,
  Calendar,
} from 'lucide-react';

// Real interior photography from /public/hero-rooms/ — swappable later via CMS.
// Every ROOMS entry now points at an AI-aligned in-situ composite where a
// real Zebra One artwork is hung on the source room's wall — no bare
// walls, no blank primed canvases. The `/hero-rooms/*.jpg` bare originals
// stay as ROOM_FALLBACKS below in case a composite ever 404s.
const ROOMS = {
  drawing: '/api/cloud-media/hero-composites/consult-drawing-audrey-hepburn-lived-in-44196d5e.png',
  study: '/api/cloud-media/hero-composites/consult-study-marianne-lived-in-5a80ee5d.png',
  dining: '/api/cloud-media/hero-composites/consult-dining-vulcano-lived-in-6585a15e.png',
  bedroom: '/api/cloud-media/hero-composites/consult-bedroom-masacz-lived-in-9dc7066d.png',
  hallway: '/api/cloud-media/hero-composites/consult-hallway-magnani-lived-in-67f1a21b.png',
  living: '/api/cloud-media/hero-composites/consult-living-studwell-marilyn-lived-in-3124e73b.png',
  library: '/api/cloud-media/hero-composites/consult-library-hirst-spot-lived-in-0f3415fc.png',
};

// If a cloud composite ever 404s (bucket cleared, key mismatch after a
// redeploy race, etc.), fall back to the bare source room so the page
// still renders instead of showing a broken image icon.
const ROOM_FALLBACKS = {
  [ROOMS.drawing]: '/hero-rooms/01_drawing_room.jpg',
  [ROOMS.study]: '/hero-rooms/02_study.jpg',
  [ROOMS.dining]: '/hero-rooms/03_dining.jpg',
  [ROOMS.bedroom]: '/hero-rooms/04_master_bedroom.jpg',
  [ROOMS.hallway]: '/hero-rooms/05_hallway.jpg',
  [ROOMS.living]: '/hero-rooms/06_living_room.jpg',
  [ROOMS.library]: '/hero-rooms/07_library.jpg',
};

const onRoomImgError = (e) => {
  const el = e.currentTarget;
  const fallback = ROOM_FALLBACKS[el.getAttribute('src')];
  if (fallback && el.dataset.fallback !== 'used') {
    el.dataset.fallback = 'used';
    el.src = fallback;
  }
};

const COMPENDIUM_ENTRIES = [
  {
    icon: Sofa,
    title: 'Comfort before aesthetics',
    body: 'A room must work before it can be beautiful. Pathways clear, seating welcoming, light in the right places. Aesthetics follow function — never the other way around.',
    image: ROOMS.drawing,
    caption: 'Drawing room · Norman Parkinson, Audrey Hepburn',
  },
  {
    icon: PenTool,
    title: 'Every room needs an anchor',
    body: 'A fireplace, a view, a single commanding artwork. Without a focal point, the eye has nowhere to settle and a room feels restless. We hang the anchor first; everything else arranges itself around it.',
    image: ROOMS.dining,
    caption: 'Dining · Jordi Díaz Alamà, Vulcano',
  },
  {
    icon: Palette,
    title: 'The 60·30·10 colour rule',
    body: '60% dominant tone (walls and floors). 30% secondary (upholstery, curtains). 10% accent (cushions, art, accessories). Three quiet ratios that make a room read as composed rather than decorated.',
    image: ROOMS.study,
    caption: 'Study · Terry O\'Neill, Marianne Faithfull',
  },
  {
    icon: Lightbulb,
    title: 'The 3·5·7 styling rule',
    body: 'Group accessories — vases, books, candles, cushions — in odd numbers. Threes, fives, sevens. The eye reads odd-numbered groupings as natural arrangements, not stiffly arranged ones.',
    image: ROOMS.living,
    caption: 'Living room · Studwell Diamond Dust',
  },
  {
    icon: Ruler,
    title: 'Hang at 145cm',
    body: 'The centre of an artwork sits at roughly 145cm from the floor — eye level for the average adult — regardless of ceiling height or sofa height. Most works hang too high; this single rule fixes it.',
    image: ROOMS.bedroom,
    caption: 'Bedroom · Helen Masacz oil painting',
  },
  {
    icon: Sun,
    title: 'Layered lighting',
    body: 'Never one source. Combine ambient (overhead), task (reading and desk), and accent (picture lights, spots). A single overhead bulb flattens a room; layers give it depth, warmth, and quiet drama after dark.',
    image: ROOMS.hallway,
    caption: 'Hallway · Sebastian Magnani, Cotton Candy',
  },
  {
    icon: PenTool,
    title: 'Mix old and new',
    body: 'A modern sofa beside an antique console. A contemporary print over a Georgian fireplace. Tension between eras is what gives a home personality — and stops it ageing the moment fashion turns.',
    image: ROOMS.library,
    caption: 'Library snug · Damien Hirst spots',
  },
];

const PROCESS_STEPS = [
  {
    n: '01',
    title: 'Free consultation',
    body: 'Share room photographs, style ideas, and budget. A complimentary, open conversation — no obligation.',
  },
  {
    n: '02',
    title: 'We analyse',
    body: 'Our curators study light, colour, and scale, then handpick works from the Zebra One collection that truly fit the space.',
  },
  {
    n: '03',
    title: 'Personalised advice',
    body: 'You receive a shortlist with mockups on your own wall. We explain why each piece works — grounded in design, not opinion.',
  },
  {
    n: '04',
    title: 'Try & decide',
    body: 'Approve, swap, or request alternatives. Once you are happy, we handle framing, delivery, and installation.',
  },
];

// ───────────────── Consultation modal ─────────────────

// ───────────────── Page ─────────────────
const ArtConsultationPage = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="art-consultation-page">
      <Header />

      {/* ─────────────────── HERO — cinematic full-bleed ─────────────────── */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden bg-neutral-900" data-testid="consultation-hero">
        <motion.img
          src={ROOMS.drawing}
          alt="A curated drawing room — interior consultation by Zebra One Gallery"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 6, ease: [0.16, 1, 0.3, 1] }}
          onError={onRoomImgError}
        />
        {/* Editorial gradient — dark bottom for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

        <div className="relative h-full max-w-[1400px] mx-auto px-5 sm:px-10 flex flex-col justify-end pb-36 sm:pb-44">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-white/70 mb-5"
          >
            Interior Design Compendium · Established 1976
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="font-serif font-light text-white leading-[0.95] tracking-tight max-w-5xl"
            style={{ fontSize: 'clamp(2.6rem, 7vw, 6rem)' }}
            data-testid="consultation-hero-title"
          >
            Live with art that
            <br />
            <span className="italic">belongs to your room.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="text-base sm:text-lg text-white/85 leading-relaxed mt-7 max-w-xl"
          >
            A complimentary advisory service from a 50-year Hampstead gallery.
            We study your space, your light, your life — then recommend pieces from
            our own inventory that genuinely fit.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="flex flex-wrap gap-3 mt-9"
          >
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-white text-black text-xs uppercase tracking-[0.25em] hover:bg-neutral-200 transition-colors"
              data-testid="consultation-hero-cta"
            >
              Book a free consultation <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#compendium"
              className="inline-flex items-center gap-3 px-8 py-3.5 border border-white/60 text-white text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors"
            >
              Read the compendium
            </a>
          </motion.div>
        </div>

        {/* Stats strip — pinned to bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="absolute bottom-0 left-0 right-0 border-t border-white/10 backdrop-blur-md bg-black/30"
        >
          <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-5 grid grid-cols-3 gap-4">
            {[
              { v: '50', l: 'Years of advisory' },
              { v: '78', l: 'Artists represented' },
              { v: '£0', l: 'For the consultation' },
            ].map((s) => (
              <div key={s.l} className="text-white">
                <p className="font-serif text-2xl sm:text-4xl leading-none">{s.v}</p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-white/60 mt-2">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ─────────────────── EDITORIAL INTRO with image — Gabrielle du Plooy ─────────────────── */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-20 sm:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 md:pr-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-5">
              Gabrielle du Plooy · The studio approach
            </p>
            <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl mb-7">
              Comfort, then function, <span className="italic">then beauty.</span>
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed mb-5">
              Zebra One Gallery was opened in July 1976 by Lee du Plooy, Gabrielle's
              father. Gabrielle took over the gallery twenty-five years ago and has
              spent that time placing art in homes across Hampstead, Mayfair and
              Holland Park. Her studio's hierarchy never changes: a room must be
              comfortable first, functional second, beautiful last. Beauty without
              function is decoration; beauty after function is design.
            </p>
            <p className="text-base text-neutral-600 leading-relaxed mb-5">
              The team works with named rules — the 60·30·10 colour ratio, odd-numbered
              styling in threes and fives and sevens, eye-level hanging at 145cm — not
              because rules are sacred, but because they make a room read calm rather
              than considered. Once the discipline is in place, the personality has
              somewhere to live.
            </p>
            <p className="text-base text-neutral-600 leading-relaxed">
              The advisors do not sell what is in fashion. They show what will still
              feel right when fashion has moved on.
            </p>
          </div>
          <div className="md:col-span-5 relative aspect-[4/5] overflow-hidden bg-neutral-100">
            <motion.img
              src="/gabrielle-du-plooy.png"
              alt="Gabrielle du Plooy, owner & director of Zebra One Gallery, photographed in front of a zebra triptych — Hampstead, London"
              className="absolute inset-0 w-full h-full object-cover object-center"
              initial={{ scale: 1.05 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              loading="lazy"
              decoding="async"
            />
            <div className="absolute bottom-4 left-4 text-white/90 text-[10px] uppercase tracking-[0.3em] mix-blend-difference">
              Gabrielle du Plooy · Owner & Director, Zebra One Gallery
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── COMPENDIUM — image-led grid ─────────────────── */}
      <section id="compendium" className="border-b border-border bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-20 sm:py-28">
          <div className="max-w-3xl mb-14 sm:mb-20">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              The seven rules
            </p>
            <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl">
              Seven principles, <span className="italic">fifty years</span> of watching them
              land.
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed mt-6 max-w-2xl">
              The rules Gabrielle du Plooy and her team return to on every consultation —
              named, specific, and tested in real homes. Comfort first, then function,
              then the careful choreography of colour, scale, light, and time.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 sm:gap-y-20">
            {COMPENDIUM_ENTRIES.map(({ icon: Icon, title, body, image, caption }, i) => (
              <motion.article
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                className="group"
                data-testid={`compendium-${title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-neutral-200 mb-5">
                  <img
                    src={image}
                    alt={`${title} — ${caption}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                    onError={onRoomImgError}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-90" />
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    <Icon className="w-5 h-5 text-black" strokeWidth={1.2} />
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white/85 text-[10px] uppercase tracking-[0.3em]">
                    {caption}
                  </div>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl leading-tight mb-3">{title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── RULES TO BREAK — editorial coda ─────────────────── */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-16 sm:py-24">
          <div className="max-w-3xl mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              And the rules to break
            </p>
            <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl">
              If you love it, <span className="italic">it never goes out of style.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 sm:gap-12">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">No. 01</p>
              <h3 className="font-serif text-xl mb-3">Wallpaper in a bathroom</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                It works — provided it sits outside the direct shower spray. Pattern
                in the most utilitarian room is one of design's quiet pleasures.
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">No. 02</p>
              <h3 className="font-serif text-xl mb-3">Bold work in a small room</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Counter-intuitive but true: a single large, confident piece in a
                modest space reads more luxurious than a wall of small, polite ones.
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">No. 03</p>
              <h3 className="font-serif text-xl mb-3">Trust the eye over the rule</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Rules give a room its discipline. Instinct gives it its soul. The
                advisor's job is to know when to apply which — and when to let the
                client overrule both.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── BEFORE / AFTER — visual transformation ─────────────────── */}
      <section className="border-b border-border bg-neutral-50" data-testid="consultation-before-after-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-16 sm:py-24">
          <div className="max-w-3xl mb-10 sm:mb-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              See the difference
            </p>
            <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl">
              The same room — <span className="italic">drag to compare.</span>
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 leading-relaxed mt-6 max-w-2xl">
              Walls finish a room. A single, considered piece changes the
              gravity of a space — the proportions, the eye-line, the
              conversation a chair has with a painting. These are the
              things a consultation gets right.
            </p>
          </div>

          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-sm border border-border">
            <img
              src="/api/cloud-media/hero-composites/consult-drawing-bosman-v3-preview-0f92d120.png"
              alt='A Hampstead drawing room curated by Zebra One Gallery — Philip Bosmans "Girl With the Orange Balaclava" hung above a boucle sofa'
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={onRoomImgError}
              data-testid="consult-drawing-in-situ"
            />
            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-black/60 backdrop-blur-sm text-white text-xs sm:text-sm px-3 py-2 rounded-sm">
              Drawing room · Philip Bosmans, <em>Girl With the Orange Balaclava</em>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── EDITORIAL SPLIT — quote + dining room ─────────────────── */}
      <section className="border-b border-border bg-black text-white relative">
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[640px] order-2 md:order-1">
            <img
              src={ROOMS.dining}
              alt="A dining room curated around a single, anchoring artwork"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={onRoomImgError}
            />
          </div>
          <div className="px-6 sm:px-12 lg:px-20 py-20 sm:py-28 flex flex-col justify-center order-1 md:order-2">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
              On placement
            </p>
            <blockquote
              className="font-serif font-light italic leading-[1.15] tracking-tight"
              style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)' }}
            >
              "An artwork doesn't sit on a wall. It sets the temperature of the
              entire room — the way you walk through it, the way you sit, the
              quiet of every conversation that happens beside it."
            </blockquote>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-px bg-white/40" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">
                Gabrielle du Plooy, Owner & Director
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── PROCESS — split layout w/ sticky image ─────────────────── */}
      <section id="consultation" className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-20 sm:py-28">
          <div className="max-w-3xl mb-14">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              How we work
            </p>
            <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl">
              Free consultation,
              <br />
              <span className="italic">then expert advice.</span>
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed mt-6 max-w-xl">
              No guesswork. No pressure. You talk to us about your space — we analyse
              and recommend work from our real inventory.
            </p>
          </div>

          <div className="grid md:grid-cols-12 gap-10 lg:gap-16">
            <div className="md:col-span-5 lg:col-span-5">
              <div className="md:sticky md:top-24">
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={ROOMS.bedroom}
                    alt="A master bedroom hang — light study before placement"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={onRoomImgError}
                  />
                  <div className="absolute bottom-4 left-4 text-white/90 text-[10px] uppercase tracking-[0.3em]">
                    Light study · master bedroom
                  </div>
                </div>
              </div>
            </div>

            <ol className="md:col-span-7 lg:col-span-7 space-y-12 sm:space-y-14">
              {PROCESS_STEPS.map((s, i) => (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="border-t border-neutral-200 pt-7"
                  data-testid={`process-step-${s.n}`}
                >
                  <div className="flex items-baseline gap-6">
                    <p className="font-serif text-2xl text-neutral-300 leading-none shrink-0">{s.n}</p>
                    <div>
                      <h3 className="font-serif text-2xl sm:text-3xl leading-tight mb-3">{s.title}</h3>
                      <p className="text-base text-neutral-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>

          <div className="mt-14 sm:mt-16">
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors"
              data-testid="consultation-process-cta"
            >
              <Calendar className="w-4 h-4" /> Book your free consultation
            </button>
          </div>
        </div>
      </section>

      {/* ─────────────────── INVENTORY POINTER ─────────────────── */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-20 sm:py-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 relative aspect-[4/5] md:aspect-[5/4] overflow-hidden">
            <motion.img
              src={ROOMS.hallway}
              alt="A Mayfair hallway curated by Zebra One Gallery"
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ scale: 1.05 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              onError={onRoomImgError}
            />
            <div className="absolute bottom-4 left-4 text-white/90 text-[10px] uppercase tracking-[0.3em]">
              Hallway · Mayfair installation
            </div>
          </div>

          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              The inventory
            </p>
            <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl mb-6">
              Your shortlist, drawn from{' '}
              <span className="italic">the real collection.</span>
            </h2>
            <p className="text-base text-neutral-600 leading-relaxed mb-7">
              No samples. No stock imagery. During your consultation we pull the
              specific pieces that suit your light, scale, and palette — from over 1,000
              catalogued works across 78 artists.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/shop"
                className="inline-flex items-center gap-3 px-8 py-3.5 border border-black text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-colors"
                data-testid="consultation-browse-collection"
              >
                Browse the collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/artists"
                className="inline-flex items-center gap-3 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-neutral-700 hover:text-black"
              >
                Meet the artists
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── FINAL CTA ─────────────────── */}
      <section className="relative h-[60vh] min-h-[460px] overflow-hidden bg-neutral-900" data-testid="consultation-final-cta">
        <img
          src={ROOMS.drawing}
          alt="Begin your art consultation with Zebra One Gallery"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          onError={onRoomImgError}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative h-full max-w-[1400px] mx-auto px-5 sm:px-10 flex flex-col items-center justify-center text-center text-white">
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/70 mb-5">
            Begin
          </p>
          <h2
            className="font-serif font-light leading-[1.05] tracking-tight max-w-3xl"
            style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}
          >
            Tell us about <span className="italic">your room.</span>
          </h2>
          <p className="text-base sm:text-lg text-white/80 leading-relaxed mt-6 max-w-lg">
            A 30-minute conversation with a senior advisor. We'll listen first.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="mt-10 inline-flex items-center gap-3 px-9 py-4 bg-white text-black text-xs uppercase tracking-[0.25em] hover:bg-neutral-200 transition-colors"
            data-testid="consultation-final-cta-btn"
          >
            <Calendar className="w-4 h-4" /> Book a free consultation
          </button>
        </div>
      </section>

      <Footer />
      <ConsultationModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default ArtConsultationPage;
